import { FormControl, InputLabel } from "@mui/material";

import MyDialog from "~/mui/MyDialog";
import { useTrans } from '~/hooks/useTrans';
import { apiUpdateProjectCatalogAdminSettings } from '~/providers/api';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { IClientProjectCatalog } from "~/providers/client-interfaces";
import { IUrlParams } from "~/interfaces";
import { MySelect, MyMenuItem } from "~/mui/MySelect";
import CatalogQualitySelect from "~/components/CatalogQualitySelect";
import { TCatalogQuality } from "~/providers/interfaces";

import "./style.less";

type TApproved = "null" | "true" | "false";

interface IState extends Pick<IClientProjectCatalog, "quality"> {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
	approvedByAdmin: TApproved;
}

interface IAdminProjectCatalogModal {
	params: IUrlParams;
	catalogData: IClientProjectCatalog;
	onClose?: () => void;
	onSave?: (catalog: IClientProjectCatalog) => void;
	onChange?: (isChanged: boolean) => void;
}

function approvedByAdminToString(approved: IClientProjectCatalog["approvedByAdmin"]): TApproved {
	if (approved === null) {
		return "null";
	}

	return approved === true
		? "true"
		: "false";
}

function approvedByAdminParseValue(approved: TApproved): IClientProjectCatalog["approvedByAdmin"] {
	switch (approved) {
		case "false":
			return false;

		case "null":
			return null;

		default:
	}

	return true;
}

export default function AdminProjectCatalogModal({
	params,
	catalogData = null,
	onClose = () => {},
	onSave = () => {},
	onChange = () => {},
}: IAdminProjectCatalogModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const defApprovedByAdmin = approvedByAdminToString(catalogData.approvedByAdmin);
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
		approvedByAdmin: defApprovedByAdmin,
		quality: catalogData.quality,
	});

	function updateData(changedData: Partial<IState>) {
		const newState = {
			...state,
			...changedData,
		};
		const wasChanged = newState.approvedByAdmin !== defApprovedByAdmin
			|| newState.quality !== catalogData.quality;

		updateState({
			...changedData,
			wasChanged,
		});
		onChange(wasChanged);
	}

	async function dialogOnSave() {
		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const outputData = await apiUpdateProjectCatalogAdminSettings(params.projectId, catalogData.id, {
			approvedByAdmin: approvedByAdminParseValue(state.approvedByAdmin),
			quality: state.quality,
		}, lang);

		if (outputData.noErrors) {
			updateState({
				dialogLoading: false,
			});
			onSave(outputData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("adminProjectCatalogModal.mainTitle")} onClose={onClose} onSave={dialogOnSave} contentClassName="adminProjectCatalogModal" dialogLoading={state.dialogLoading}
		errorMsg={state.errorMsg} saveCaption={trans("saveChanges")} cancelCaption={trans("close")} confirmClose={state.wasChanged}>
		<FormControl size="small">
			<InputLabel>{ trans("adminProjectCatalogModal.approvedByAdmin.title") }</InputLabel>
			<MySelect
				value={state.approvedByAdmin}
				onChange={event => updateData({ approvedByAdmin: event.target.value as TApproved })}
				label={ trans("adminProjectCatalogModal.approvedByAdmin.title") }
			>
				<MyMenuItem value="null">{ trans("adminProjectCatalogModal.approvedByAdmin.value.null") }</MyMenuItem>
				<MyMenuItem value="false">{ trans("adminProjectCatalogModal.approvedByAdmin.value.false") }</MyMenuItem>
				<MyMenuItem value="true">{ trans("adminProjectCatalogModal.approvedByAdmin.value.true") }</MyMenuItem>
			</MySelect>
		</FormControl>
		<CatalogQualitySelect value={state.quality} hasNone={false} onChange={(quality: TCatalogQuality) => updateData({ quality })} />
	</MyDialog>;
}
